// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { useLocalMessage } from '@Helpers/locale';
import client from '@Client';
import { THLayout } from '@Molecules/THLayout';
import { THList } from '@Molecules/THList';
import useConfigList from '@Hooks/useConfigList';
import { ObjectTypeV1, OrgPermissionsEnum, PermissionsEnum, AttachmentType } from 'thehive-sdk';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { AttachmentsCreate } from '@Organisms/Attachments/AttachmentsCreate/AttachmentsCreate';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { ColumnTitle } from '@Atoms/ColumnTitle/ColumnTitle';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { formatFileSize } from '@Helpers/stats';
import { FormatDate } from '@Molecules/Format';
import { copyToClipboard } from '@Helpers/copy';
import { ICONS } from '@Constants/iconsApp';
import ExternalLink from '@Atoms/ExternalLink/ExternalLink';
import useUserPermissions from '@Hooks/useUserPermissions';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { StorageFilterEnum } from '@Hooks/useStoragePage';

import './AttachmentList.scss';

const AttachmentList = (): ReactElement | null => {
    const i = useLocalMessage();

    const { orgId } = useParams<{ orgId: string }>();

    const hasPermissions = useUserPermissions({
        permissionsNeeded: [OrgPermissionsEnum.MANAGE_KNOWLEDGE_BASE],
    });

    const { dataSource, paginationManager, table, listRefetch, countRefetch, columnsRenderer } =
        useConfigList<AttachmentType>({
            selector: {
                head: [{ name: 'getOrganisation', idOrName: orgId }, { name: 'attachments' }],
                name: `get-${orgId}-attachments`,
            },
            controller: client.CaseController,
            objectType: ObjectTypeV1.ORGANISATION,
            storageId: StorageFilterEnum.ATTACHMENTS,
        });

    const columns = columnsRenderer([
        {
            title: <ColumnTitle>{i('case.attachment.table.filename')}</ColumnTitle>,
            className: 'attachment-list__column-filename custom-table__cell',
            render: ({ name }): ReactElement => <span>{name}</span>,
        },
        {
            title: <ColumnTitle>{i('case.attachment.table.type')}</ColumnTitle>,
            className: 'attachment-list__column-type custom-table__cell',
            render: ({ contentType }): ReactElement => <span>{contentType}</span>,
        },
        {
            title: <ColumnTitle>{i('case.attachment.table.size')}</ColumnTitle>,
            className: 'attachment-list__column-size custom-table__cell',
            render: ({ size }): ReactElement => <span>{formatFileSize(size)}</span>,
        },
        {
            title: <ColumnTitle>{i('case.attachment.table.date')}</ColumnTitle>,
            className: 'attachment-list__column-date custom-table__cell',
            render: ({ _createdAt = 0 }): ReactElement => <FormatDate date={_createdAt} />,
        },
        {
            className: 'attachment-list__column-download custom-table__cell',
            render: ({ _id }): ReactElement => (
                <ActionsList
                    actionList={{
                        copy: {
                            label: i('general.copy-url'),
                            icon: <ICONS.CopyOutlined />,
                            query: () =>
                                copyToClipboard(
                                    window.location.origin + client.apiUrl + `/v1/attachment/${_id}`,
                                    i('notification.copy'),
                                ),
                        },
                        export: {
                            label: i('general.download'),
                            icon: <ICONS.DownloadOutlined />,
                            CustomComponent: (triggerElem): ReactElement => (
                                <ExternalLink url={window.location.origin + client.apiUrl + `/v1/attachment/${_id}`}>
                                    {triggerElem}
                                </ExternalLink>
                            ),
                        },
                        delete: {
                            label: i('general.remove'),
                            message: i('attachment.action.confirm.delete'),
                            hidden: !hasPermissions,
                            icon: <ICONS.DeleteIcon />,
                            confirm: true,
                            query: () => {
                                client.AttachmentController.delete(_id)
                                    .then(() => {
                                        listRefetch();
                                        countRefetch();
                                        openMessage('success', i('notification.attachment.delete'));
                                    })
                                    .catch(handleNotificationError);
                            },
                        },
                    }}
                    scope="single"
                    isIcon
                />
            ),
        },
    ]);

    return (
        <THLayout>
            <THList>
                <THList.Header>
                    <THList.Slot name="MANAGE">
                        {hasPermissions && (
                            <AttachmentsCreate
                                triggerElem={<AddButton label={i('attachment.sider.add')} />}
                                onAddAttachment={(formData) =>
                                    client.AttachmentController.create(formData)
                                        .then(() => {
                                            listRefetch();
                                            countRefetch();
                                            openMessage('success', i('notification.attachment.create'));
                                        })
                                        .catch(handleNotificationError)
                                }
                            />
                        )}
                    </THList.Slot>
                </THList.Header>
                <THList.Content
                    dataSource={dataSource}
                    columns={columns}
                    rowKey="_id"
                    emptyPlaceholder={
                        <DisplayEmptyData>
                            {hasPermissions && (
                                <AttachmentsCreate
                                    triggerElem={<THButton type="link">{i('attachment.sider.add')}</THButton>}
                                    onAddAttachment={(formData) =>
                                        client.AttachmentController.create(formData)
                                            .then(() => {
                                                listRefetch();
                                                countRefetch();
                                                openMessage('success', i('notification.attachment.create'));
                                            })
                                            .catch(handleNotificationError)
                                    }
                                />
                            )}
                        </DisplayEmptyData>
                    }
                />
                <THList.Footer>
                    <THList.Slot name="NAVIGATION">
                        <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                        <PaginationSize paginationConfig={paginationManager} />
                    </THList.Slot>
                </THList.Footer>
            </THList>
        </THLayout>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'case.layout.nav-bar.attachments',
        icon: <ICONS.FileSearchOutlined />,
    },
    path: RouteBuilder.compute('organisation.attachments'),
    permissionNeeded: [PermissionsEnum.MANAGE_KNOWLEDGE_BASE],
    content: <AttachmentList />,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.case.attachments' },
};
